import { useEffect } from "react"
import { motion } from "framer-motion"
import { CheckCircle2, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"
import { GlassButton } from "@/components/ui/GlassButton"

interface SuccessCardProps {
  referenceId: string
  /** Pode vir indefinido no fluxo de processamento em segundo plano —
   *  o título só é gerado quando a IA processa, alguns segundos depois. */
  uiTitle?: string | null
  clickupTaskUrl?: string | null
  warning?: string
  onSendAnother: () => void
  onClose: () => void
}

export function SuccessCard({
  referenceId,
  uiTitle,
  clickupTaskUrl,
  warning,
  onSendAnother,
  onClose,
}: SuccessCardProps) {
  // Auto-close em 8s se nada acontecer
  useEffect(() => {
    const t = setTimeout(onClose, 8000)
    return () => clearTimeout(t)
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className="space-y-5 py-2"
    >
      {/* Check animado */}
      <div className="flex flex-col items-center gap-3 text-center">
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 20 }}
          className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center",
            "bg-emerald-500/15",
          )}
        >
          <CheckCircle2 className="w-8 h-8 text-emerald-500" strokeWidth={2.5} />
        </motion.div>

        <div className="space-y-1">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">
            Recebemos! 🎉
          </h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            Nosso time já foi notificado. Você vai ver uma notificação aqui assim
            que estivermos com isso resolvido.
          </p>
        </div>
      </div>

      {/* Card com info */}
      <div
        className={cn(
          "p-3 rounded-xl backdrop-blur-[16px] bg-glass-card border border-glass-border",
          "space-y-2",
        )}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)] font-medium">
            Referência
          </span>
          <code className="text-xs font-mono font-semibold text-[var(--text-primary)]">
            #{referenceId}
          </code>
        </div>
        {uiTitle && (
          <p className="text-xs text-[var(--text-secondary)] line-clamp-2">
            {uiTitle}
          </p>
        )}
        {clickupTaskUrl && (
          <a
            href={clickupTaskUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-1.5 text-[11px] font-medium",
              "text-[var(--accent-blue,#2563eb)] hover:underline",
            )}
          >
            Ver no ClickUp
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {warning && (
        <p className="text-[11px] text-amber-600 text-center">
          Salvamos sua mensagem mas tivemos um problema ao avisar o time. Vamos
          retomar manualmente em breve.
        </p>
      )}

      {/* Ações */}
      <div className="flex flex-col gap-2">
        <GlassButton variant="ghost" size="sm" onClick={onSendAnother}>
          Enviar outro feedback
        </GlassButton>
      </div>
    </motion.div>
  )
}
